import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Input, Select } from '@/components/common';
import {
  EDUCATION_QUALIFICATION_OPTIONS,
  EMPLOYMENT_TYPE_OPTIONS,
  LIFE_BENEFIT_OPTION_OPTIONS,
  MEDICAL_STATUS_OPTIONS,
  PLAN_VARIANT_OPTIONS,
  PREMIUM_PAYMENT_FREQUENCY_OPTIONS,
  PROPOSER_RELATIONSHIP_OPTIONS,
  type LifeQuoteFormData,
} from '../../schema';
import { YesNoToggle } from './YesNoToggle';

const POLICY_TERM_OPTIONS = Array.from({ length: 40 }, (_, i) => ({
  value: String(i + 1),
  label: `${i + 1} year${i === 0 ? '' : 's'}`,
}));

export function ProductDetailsStep() {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<LifeQuoteFormData>();

  const policyTermYears = watch('policy_term_years');
  const premiumPaymentTermYears = watch('premium_payment_term_years');
  const existingCustomer = watch('existing_customer');
  const firstTimeBuyer = watch('first_time_buyer');
  const premiumHoliday = watch('premium_holiday');
  const smokingTobaccoUsage = watch('smoking_tobacco_usage');

  // Premium Payment Term can never exceed the chosen Policy Term.
  const premiumPaymentTermOptions = useMemo(() => {
    const maxTerm = Number(policyTermYears) || 40;
    return POLICY_TERM_OPTIONS.filter((o) => Number(o.value) <= maxTerm);
  }, [policyTermYears]);

  // If a shorter Policy Term makes the current selection invalid, clear it
  // rather than silently submitting a value the dropdown no longer offers.
  useEffect(() => {
    if (
      premiumPaymentTermYears &&
      !premiumPaymentTermOptions.some((o) => o.value === premiumPaymentTermYears)
    ) {
      setValue('premium_payment_term_years', '', { shouldValidate: false });
    }
  }, [premiumPaymentTermOptions, premiumPaymentTermYears, setValue]);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Product Details</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">
          Tell us about the policy and cover you&rsquo;re looking for.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="Policy Term"
            options={POLICY_TERM_OPTIONS}
            placeholder="Select policy term"
            required
            error={errors.policy_term_years?.message}
            {...register('policy_term_years')}
          />
          <Select
            label="Premium Payment Term"
            options={premiumPaymentTermOptions}
            placeholder="Select premium payment term"
            required
            error={errors.premium_payment_term_years?.message}
            {...register('premium_payment_term_years')}
          />
          <Select
            label="Premium Payment Frequency"
            options={PREMIUM_PAYMENT_FREQUENCY_OPTIONS}
            placeholder="Select frequency"
            required
            error={errors.premium_payment_frequency?.message}
            {...register('premium_payment_frequency')}
          />
          <Select
            label="Proposer Relationship"
            options={PROPOSER_RELATIONSHIP_OPTIONS}
            placeholder="Relationship to Life Assured"
            required
            error={errors.proposer_relationship?.message}
            {...register('proposer_relationship')}
          />
          <Select
            label="Plan Variant"
            options={PLAN_VARIANT_OPTIONS}
            placeholder="Select plan variant"
            required
            error={errors.plan_variant?.message}
            {...register('plan_variant')}
          />
          <Select
            label="Life Benefit Option"
            options={LIFE_BENEFIT_OPTION_OPTIONS}
            placeholder="Select benefit option"
            required
            error={errors.life_benefit_option?.message}
            {...register('life_benefit_option')}
          />
        </div>
      </div>

      <div className="border-t border-gray-100 dark:border-gray-700 pt-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Eligibility</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <YesNoToggle
            label="Existing Customer"
            required
            value={existingCustomer}
            onChange={(v) => setValue('existing_customer', v, { shouldValidate: true, shouldDirty: true })}
            error={errors.existing_customer?.message}
          />
          <YesNoToggle
            label="First Time Buyer"
            required
            value={firstTimeBuyer}
            onChange={(v) => setValue('first_time_buyer', v, { shouldValidate: true, shouldDirty: true })}
            error={errors.first_time_buyer?.message}
          />
          <YesNoToggle
            label="Premium Holiday"
            required
            value={premiumHoliday}
            onChange={(v) => setValue('premium_holiday', v, { shouldValidate: true, shouldDirty: true })}
            error={errors.premium_holiday?.message}
          />
          <YesNoToggle
            label="Smoking / Tobacco Usage"
            required
            value={smokingTobaccoUsage}
            onChange={(v) => setValue('smoking_tobacco_usage', v, { shouldValidate: true, shouldDirty: true })}
            error={errors.smoking_tobacco_usage?.message}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="Medical / Non-Medical"
            options={MEDICAL_STATUS_OPTIONS}
            placeholder="Select status"
            required
            error={errors.medical_non_medical?.message}
            {...register('medical_non_medical')}
          />
        </div>
      </div>

      <div className="border-t border-gray-100 dark:border-gray-700 pt-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Occupation &amp; Finances</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Occupation"
            required
            error={errors.occupation?.message}
            {...register('occupation')}
          />
          <Select
            label="Employment Type"
            options={EMPLOYMENT_TYPE_OPTIONS}
            placeholder="Select employment type"
            required
            error={errors.employment_type?.message}
            {...register('employment_type')}
          />
          <Select
            label="Education Qualification"
            options={EDUCATION_QUALIFICATION_OPTIONS}
            placeholder="Select qualification"
            required
            error={errors.education_qualification?.message}
            {...register('education_qualification')}
          />
          <Input
            label="Annual Income"
            type="number"
            min={0}
            required
            leftIcon={<span className="text-gray-500 dark:text-gray-400">₹</span>}
            error={errors.annual_income?.message}
            {...register('annual_income')}
          />
          <Input
            label="Sum Assured"
            type="number"
            min={0}
            required
            leftIcon={<span className="text-gray-500 dark:text-gray-400">₹</span>}
            error={errors.sum_assured?.message}
            {...register('sum_assured')}
          />
        </div>
      </div>
    </div>
  );
}
